/**
 * Instant Quote
 *
 * Live screen-printing price calculator, bridged into a Powerful Form Builder
 * form so what the customer submits is what they priced.
 *
 * All money is handled in integer cents. Floating point would drift, and on a
 * 900-piece order that drift becomes a visibly wrong total.
 */

const STATUS = {
  OK: 'ok',
  NO_PRICING: 'no-pricing',
  NO_QUANTITY: 'no-quantity',
  BELOW_MIN: 'below-min',
  ABOVE_MAX: 'above-max',
  NO_LOCATIONS: 'no-locations',
  COLOUR_GATE: 'colour-gate',
};

function chartRange(pricing) {
  const brackets = pricing.brackets || [];
  if (!brackets.length) return null;
  return {
    min: Math.min(...brackets.map((b) => b.min)),
    max: Math.max(...brackets.map((b) => b.max)),
  };
}

function findBracket(pricing, qty) {
  return (pricing.brackets || []).find((b) => qty >= b.min && qty <= b.max) || null;
}

function minQuantityForColours(pricing, colours) {
  const idx = colours - 1;
  const found = (pricing.brackets || [])
    .filter((b) => b.rates[idx] !== null && b.rates[idx] !== undefined)
    .sort((a, b) => a.min - b.min)[0];
  return found ? found.min : null;
}

/**
 * Garment cost, split by size price. With a size breakdown each size is
 * charged at its own price (2XL at the 2X price, and so on); without one the
 * whole order is charged at the base price. A missing 2X or 3X price falls
 * back to the base price.
 */
function garmentLines(garment, state, pricing, qty) {
  const prices = garment.prices || { base: garment.unitCents };
  const base = prices.base || 0;
  if (base <= 0) return [];

  const counts = { base: 0, x2: 0, x3: 0 };
  let sized = 0;
  (pricing.sizes || []).forEach((size) => {
    const count = state.sizes[size.id] || 0;
    const tier = counts[size.tier] !== undefined ? size.tier : 'base';
    counts[tier] += count;
    sized += count;
  });
  if (!sized) counts.base = qty;

  const tiers = pricing.tiers || {};
  return ['base', 'x2', 'x3']
    .filter((tier) => counts[tier] > 0)
    .map((tier) => {
      const unitCents = prices[tier] > 0 ? prices[tier] : base;
      return {
        tier,
        tierLabel: tiers[tier] || tier,
        count: counts[tier],
        unitCents,
        lineCents: counts[tier] * unitCents,
      };
    });
}

function buildQuote(state, pricing) {
  const result = {
    status: STATUS.OK,
    qty: state.quantity,
    lines: [],
    printCents: 0,
    garmentCents: 0,
    garmentLines: [],
    garmentSized: false,
    garmentUnsized: false,
    subtotalCents: 0,
    surchargeBaseCents: 0,
    surchargeCents: 0,
    setupCents: 0,
    totalCents: 0,
    perUnitCents: 0,
    perUnitPrintCents: 0,
    gates: [],
    suggestedQty: null,
    garment: null,
    premiumInk: false,
  };

  const range = chartRange(pricing);
  if (!range) {
    result.status = STATUS.NO_PRICING;
    return result;
  }

  const qty = Number(state.quantity);
  if (!qty || qty < 1) {
    result.status = STATUS.NO_QUANTITY;
    return result;
  }

  const active = (pricing.locations || [])
    .map((loc) => ({ loc, colours: state.locations[loc.id] || 0 }))
    .filter((entry) => entry.colours > 0);

  if (!active.length) {
    result.status = STATUS.NO_LOCATIONS;
    return result;
  }

  if (qty < range.min) {
    result.status = STATUS.BELOW_MIN;
    result.suggestedQty = range.min;
    return result;
  }

  if (qty > range.max) {
    result.status = STATUS.ABOVE_MAX;
    return result;
  }

  const bracket = findBracket(pricing, qty);
  if (!bracket) {
    result.status = STATUS.NO_PRICING;
    return result;
  }

  const gateBasis = pricing.gateBasis === 'total' ? 'total' : 'location';

  if (gateBasis === 'total') {
    const totalColours = active.reduce((sum, e) => sum + e.colours, 0);
    if (totalColours > pricing.maxColors) {
      result.status = STATUS.ABOVE_MAX;
      return result;
    }
    if (bracket.rates[totalColours - 1] === null || bracket.rates[totalColours - 1] === undefined) {
      result.gates.push({ label: `${totalColours} colours in total`, colours: totalColours });
    }
  } else {
    active.forEach((entry) => {
      const rate = bracket.rates[entry.colours - 1];
      if (rate === null || rate === undefined) {
        result.gates.push({
          label: entry.loc.label,
          colours: entry.colours,
          locationId: entry.loc.id,
        });
      }
    });
  }

  if (result.gates.length) {
    result.status = STATUS.COLOUR_GATE;
    const needed = result.gates
      .map((g) => minQuantityForColours(pricing, g.colours))
      .filter((n) => n !== null);
    result.suggestedQty = needed.length ? Math.max(...needed) : null;
    result.gates.forEach((g) => {
      g.minQty = minQuantityForColours(pricing, g.colours);
    });
    return result;
  }

  active.forEach((entry) => {
    const rateCents = bracket.rates[entry.colours - 1];
    const lineCents = qty * rateCents;
    result.printCents += lineCents;
    result.perUnitPrintCents += rateCents;
    result.lines.push({
      id: entry.loc.id,
      label: entry.loc.label,
      colours: entry.colours,
      rateCents,
      lineCents,
    });
  });

  if (pricing.garmentMode === 'add' && state.garmentId) {
    const garment = (pricing.garments || []).find((g) => g.id === state.garmentId);
    const lines = garment ? garmentLines(garment, state, pricing, qty) : [];
    if (lines.length) {
      result.garment = garment;
      result.garmentLines = lines;
      result.garmentCents = lines.reduce((sum, line) => sum + line.lineCents, 0);

      const prices = garment.prices || {};
      result.garmentSized = Object.values(state.sizes || {}).some((count) => count > 0);
      result.garmentUnsized = !result.garmentSized && (prices.x2 > 0 || prices.x3 > 0);
    }
  }

  const setupPerScreen = pricing.setupFeePerScreenCents || 0;
  if (setupPerScreen > 0) {
    const screens = active.reduce((sum, e) => sum + e.colours, 0);
    result.setupCents = screens * setupPerScreen;
    result.screens = screens;
  }

  result.subtotalCents = result.printCents + result.garmentCents + result.setupCents;

  // Premium ink marks up the printing only — the garment stays at cost.
  result.surchargeBaseCents = result.printCents + result.setupCents;

  if (state.premiumInk && pricing.ink && pricing.ink.enabled) {
    result.premiumInk = true;
    result.surchargeCents = Math.round((result.surchargeBaseCents * pricing.ink.percent) / 100);
  }

  result.totalCents = result.subtotalCents + result.surchargeCents;
  result.perUnitCents = Math.round(result.totalCents / qty);

  return result;
}

class InstantQuote extends HTMLElement {
  #pricing = null;
  #state = {
    garmentId: null,
    garmentColor: null,
    garmentColorLabel: 'Colour',
    quantity: 26,
    locations: {},
    premiumInk: false,
    sizes: {},
  };
  #formFields = null;
  #formObserver = null;
  #money = null;

  connectedCallback() {
    this.#pricing = this.#readPricing();
    if (!this.#pricing) return;

    this.#money = new Intl.NumberFormat(this.#pricing.locale || 'en-US', {
      style: 'currency',
      currency: this.#pricing.currency || 'USD',
    });

    this.#seedState();
    this.#bindEvents();
    this.#watchForm();
    this.#update();

    this.setAttribute('loaded', '');
  }

  disconnectedCallback() {
    if (this.#formObserver) this.#formObserver.disconnect();
  }

  #readPricing() {
    const node = this.querySelector('[data-instant-quote-pricing]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[instant-quote] Could not parse pricing data.', error);
      return null;
    }
  }

  #seedState() {
    const garment = this.querySelector('[data-iq-garment]:checked, select[data-iq-garment]');
    if (garment) this.#state.garmentId = garment.value;
    this.#showGarmentColors();

    const qty = this.querySelector('[data-iq-quantity]');
    if (qty) this.#state.quantity = parseInt(qty.value, 10) || 0;

    this.#readSizes();

    const ink = this.querySelector('[data-iq-ink]:checked');
    this.#state.premiumInk = ink ? ink.value === 'premium' : false;

    (this.#pricing.locations || []).forEach((loc) => {
      const row = this.querySelector(`[data-iq-location="${loc.id}"]`);
      if (!row) return;
      const toggle = row.querySelector('[data-iq-location-toggle]');
      const colour = row.querySelector('[data-iq-color-input]:checked');
      this.#state.locations[loc.id] =
        toggle && toggle.checked ? parseInt(colour ? colour.value : '1', 10) : 0;
    });
  }

  #bindEvents() {
    this.addEventListener('change', (event) => {
      const target = event.target;

      if (target.matches('[data-iq-garment]')) {
        this.#selectGarment(target.value);
      } else if (target.matches('[data-iq-garment-color]')) {
        this.#readGarmentColor();
      } else if (target.matches('[data-iq-quantity]')) {
        this.#state.quantity = parseInt(target.value, 10) || 0;
      } else if (target.matches('[data-iq-location-toggle]')) {
        this.#toggleLocation(target);
      } else if (target.matches('[data-iq-color-input]')) {
        const row = target.closest('[data-iq-location]');
        if (row) {
          this.#state.locations[row.dataset.iqLocation] = parseInt(target.value, 10) || 0;
        }
      } else if (target.matches('[data-iq-ink]')) {
        this.#state.premiumInk = target.value === 'premium';
      } else if (target.matches('[data-iq-size]')) {
        this.#readSizes();
      } else {
        return;
      }

      this.#update();
    });

    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-iq-size]')) {
        this.#readSizes();
        this.#update();
        return;
      }

      if (!event.target.matches('[data-iq-quantity]')) return;
      this.#state.quantity = parseInt(event.target.value, 10) || 0;
      this.#update();
    });

    this.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-iq-brand-tab]');
      if (!tab) return;

      const tabs = Array.from(this.querySelectorAll('[data-iq-brand-tab]'));
      const index = tabs.indexOf(tab);
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      let next = null;
      if (step) next = tabs[(index + step + tabs.length) % tabs.length];
      if (event.key === 'Home') next = tabs[0];
      if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;

      event.preventDefault();
      this.#showBrand(next);
      next.focus();
    });

    this.addEventListener('click', (event) => {
      const brandTab = event.target.closest('[data-iq-brand-tab]');
      if (brandTab) {
        this.#showBrand(brandTab);
        return;
      }

      if (event.target.closest('[data-iq-sizes-clear]')) {
        this.#clearSizes();
        this.#update();
        return;
      }

      const quick = event.target.closest('[data-iq-quick-qty]');
      if (quick) {
        this.#setQuantity(parseInt(quick.dataset.iqQuickQty, 10));
        return;
      }

      const bump = event.target.closest('[data-iq-bump-qty]');
      if (bump) {
        this.#setQuantity(parseInt(bump.dataset.iqBumpQty, 10));
        return;
      }

      const scroll = event.target.closest('[data-iq-scroll-to-form]');
      if (scroll) {
        const panel = this.querySelector('[data-iq-form-panel]');
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  #toggleLocation(toggle) {
    const row = toggle.closest('[data-iq-location]');
    if (!row) return;
    const id = row.dataset.iqLocation;
    const colours = row.querySelector('[data-iq-colors]');

    if (toggle.checked) {
      const chosen = row.querySelector('[data-iq-color-input]:checked');
      this.#state.locations[id] = parseInt(chosen ? chosen.value : '1', 10);
      if (colours) colours.hidden = false;
    } else {
      this.#state.locations[id] = 0;
      if (colours) colours.hidden = true;
    }
  }

  #setQuantity(value) {
    if (!value || Number.isNaN(value)) return;
    this.#clearSizes();
    this.#state.quantity = value;
    const input = this.querySelector('[data-iq-quantity]');
    if (input) input.value = String(value);
    this.#update();
  }

  /**
   * A brand tab shows that brand's styles. When the selected garment belongs
   * to another brand, the first style here is selected, so the garment being
   * priced is always one the customer can see.
   */
  #showBrand(tab) {
    const key = tab.dataset.iqBrandTab;

    this.querySelectorAll('[data-iq-brand-tab]').forEach((other) => {
      const active = other === tab;
      other.setAttribute('aria-selected', String(active));
      other.tabIndex = active ? 0 : -1;
    });

    let panel = null;
    this.querySelectorAll('[data-iq-brand-panel]').forEach((candidate) => {
      const active = candidate.dataset.iqBrandPanel === key;
      candidate.hidden = !active;
      if (active) panel = candidate;
    });
    if (!panel || panel.querySelector('[data-iq-garment]:checked')) return;

    const first = panel.querySelector('[data-iq-garment]');
    if (!first) return;
    first.checked = true;
    this.#selectGarment(first.value);
    this.#update();
  }

  #selectGarment(id) {
    this.#state.garmentId = id;
    this.#showGarmentColors(this.#state.garmentColor);
  }

  /**
   * Only the selected garment shows its colours, so the swatch on screen
   * always belongs to the shirt being priced. The colour already picked
   * carries over when the new garment offers it too.
   */
  #showGarmentColors(keepColor = null) {
    this.querySelectorAll('[data-iq-gcolor-group]').forEach((group) => {
      const active = group.dataset.iqGcolorGroup === this.#state.garmentId;
      group.hidden = !active;
      if (!active || !keepColor) return;

      const match = Array.from(group.querySelectorAll('[data-iq-garment-color]')).find(
        (input) => input.value === keepColor
      );
      if (match) match.checked = true;
    });
    this.#readGarmentColor();
  }

  #readGarmentColor() {
    const group = this.querySelector(
      `[data-iq-gcolor-group="${this.#state.garmentId}"]`
    );

    if (!group) {
      this.#state.garmentColor = null;
      return;
    }

    const checked = group.querySelector('[data-iq-garment-color]:checked');
    this.#state.garmentColor = checked ? checked.value : null;

    // Whatever the merchant named the option — Color, Colour, Shade.
    this.#state.garmentColorLabel = group.dataset.iqGcolorLabel || 'Colour';

    const name = group.querySelector('[data-iq-gcolor-name]');
    if (name) name.textContent = this.#state.garmentColor || '';

    if (checked) {
      this.#paintTee(checked);
      this.#showGarmentImage(checked);
    }
  }

  /** Cards without a photo draw a tee in the colour that is ticked. */
  #paintTee(input) {
    const swatch = input.parentElement.querySelector('.iq-gcolor__swatch');
    const fill = swatch ? swatch.style.getPropertyValue('--background-graphic').trim() : '';
    if (fill && !fill.startsWith('url(')) {
      this.style.setProperty('--iq-tee-color', fill);
    } else {
      this.style.removeProperty('--iq-tee-color');
    }
  }

  /** Keep the card photo on the colour that is actually ticked. */
  #showGarmentImage(input) {
    const { image: src, imageAlt: alt } = input.dataset;
    if (!src) return;

    const card = this.querySelector(
      `[data-iq-garment-card="${this.#state.garmentId}"]`
    );
    const img = card ? card.querySelector('img') : null;
    if (!img || img.currentSrc === src || img.src === src) return;

    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = src;
    if (alt) img.alt = alt;
  }

  /**
   * Sizes are the quantity whenever any are filled in: reading them writes
   * the total back into the quantity field, which stays the single value the
   * price is built from.
   */
  #readSizes() {
    let total = 0;

    (this.#pricing.sizes || []).forEach((size) => {
      const input = this.querySelector(`[data-iq-size="${size.id}"]`);
      let value = input ? parseInt(input.value, 10) : 0;
      if (!Number.isFinite(value) || value < 0) value = 0;
      this.#state.sizes[size.id] = value;
      total += value;
    });

    if (total > 0) this.#state.quantity = total;
    this.#syncQuantityField(total);
    return total;
  }

  #syncQuantityField(total) {
    const driven = total > 0;
    const input = this.querySelector('[data-iq-quantity]');
    const note = this.querySelector('[data-iq-sizes-total]');
    const noteText = this.querySelector('[data-iq-sizes-total-text]');

    if (input) {
      input.readOnly = driven;
      input.classList.toggle('iq-qty__input--derived', driven);
      if (driven) input.value = String(total);
    }

    if (note) note.hidden = !driven;
    if (noteText && driven) {
      noteText.textContent = `${total} piece${total === 1 ? '' : 's'} from the size breakdown`;
    }
  }

  #clearSizes() {
    (this.#pricing.sizes || []).forEach((size) => {
      const input = this.querySelector(`[data-iq-size="${size.id}"]`);
      if (input) input.value = '';
      this.#state.sizes[size.id] = 0;
    });
    this.#syncQuantityField(0);
  }

  #sizeParts() {
    return (this.#pricing.sizes || [])
      .filter((size) => (this.#state.sizes[size.id] || 0) > 0)
      .map((size) => `${size.label} × ${this.#state.sizes[size.id]}`);
  }

  #sizeMap() {
    const map = {};
    (this.#pricing.sizes || []).forEach((size) => {
      const value = this.#state.sizes[size.id] || 0;
      if (value > 0) map[size.label] = value;
    });
    return map;
  }

  #update() {
    const quote = buildQuote(this.#state, this.#pricing);
    this.#renderSummary(quote);
    this.#renderGates(quote);
    this.#renderInk(quote);
    this.#renderRecap(quote);
    this.#writeToForm(quote);

    this.dispatchEvent(
      new CustomEvent('on:instant-quote:change', { bubbles: true, detail: { quote } })
    );
  }

  #renderSummary(quote) {
    const priceable = quote.status === STATUS.OK;
    const linesHost = this.querySelector('[data-iq-lines]');
    const totalEl = this.querySelector('[data-iq-total]');
    const perUnitEl = this.querySelector('[data-iq-per-unit]');
    const statusEl = this.querySelector('[data-iq-status]');
    const stickyBar = this.querySelector('[data-iq-stickybar]');
    const stickyTotal = this.querySelector('[data-iq-sticky-total]');

    if (linesHost) {
      linesHost.innerHTML = '';
      if (priceable) {
        quote.lines.forEach((line) => {
          linesHost.appendChild(
            this.#summaryLine(
              `${line.label} · ${line.colours} colour${line.colours > 1 ? 's' : ''}`,
              `${this.#money.format(line.rateCents / 100)} each`,
              this.#money.format(line.lineCents / 100)
            )
          );
        });

        this.#garmentLineParts(quote).forEach(([label, meta, amount]) => {
          linesHost.appendChild(this.#summaryLine(label, meta, amount));
        });

        if (quote.setupCents > 0) {
          linesHost.appendChild(
            this.#summaryLine(
              'Setup',
              `${quote.screens} screen${quote.screens > 1 ? 's' : ''}`,
              this.#money.format(quote.setupCents / 100)
            )
          );
        }

        if (quote.surchargeCents > 0) {
          linesHost.appendChild(
            this.#summaryLine(
              this.#pricing.ink.label,
              `+${this.#pricing.ink.percent}%`,
              this.#money.format(quote.surchargeCents / 100)
            )
          );
        }
      }
    }

    const totalText = priceable ? this.#money.format(quote.totalCents / 100) : '—';
    if (totalEl) totalEl.textContent = totalText;
    if (stickyTotal) stickyTotal.textContent = totalText;

    if (perUnitEl) {
      perUnitEl.textContent = priceable
        ? `${this.#money.format(quote.perUnitCents / 100)} ${this.#pricing.text.perUnitLabel}`
        : '';
    }

    if (stickyBar) stickyBar.hidden = !priceable;

    const garmentNote = this.querySelector('[data-iq-garment-note]');
    if (garmentNote) {
      const show = priceable && quote.garmentUnsized;
      garmentNote.hidden = !show;
      garmentNote.textContent = show ? this.#unsizedNote() : '';
    }

    if (statusEl) {
      const message = this.#statusMessage(quote);
      if (message) {
        statusEl.innerHTML = message;
        statusEl.hidden = false;
      } else {
        statusEl.hidden = true;
        statusEl.innerHTML = '';
      }
    }

    this.toggleAttribute('data-priceable', priceable);
  }

  /**
   * Without a size breakdown the garment is one "each" line; with one, it is a
   * line per size price, so the customer sees what their 2XLs cost.
   */
  #garmentLineParts(quote) {
    if (!quote.garment || !quote.garmentLines.length) return [];
    const money = (cents) => this.#money.format(cents / 100);
    const label = quote.garment.label;

    if (!quote.garmentSized) {
      const line = quote.garmentLines[0];
      return [[label, `${money(line.unitCents)} each`, money(line.lineCents)]];
    }

    return quote.garmentLines.map((line) => [
      `${label} · ${line.tierLabel}`,
      `${line.count} × ${money(line.unitCents)}`,
      money(line.lineCents),
    ]);
  }

  #unsizedNote() {
    const tiers = this.#pricing.tiers || {};
    return `Garments priced at the ${tiers.base || 'base'} rate. Add a size breakdown and ${
      tiers.x2 || '2X'
    } and ${tiers.x3 || '3X'} sizes are priced at their own rate.`;
  }

  #summaryLine(label, meta, amount) {
    const row = document.createElement('div');
    row.className = 'iq-summary__line';
    row.innerHTML = `
      <span class="iq-summary__line-label">${escapeHtml(label)}</span>
      <span class="iq-summary__line-meta">${escapeHtml(meta)}</span>
      <span class="iq-summary__line-amount">${escapeHtml(amount)}</span>
    `;
    return row;
  }

  #statusMessage(quote) {
    const range = chartRange(this.#pricing);

    switch (quote.status) {
      case STATUS.NO_PRICING:
        return 'We can’t price this online just now — send us your details and we’ll quote it directly.';

      case STATUS.NO_QUANTITY:
        return 'Enter how many pieces you need.';

      case STATUS.NO_LOCATIONS:
        return 'Choose at least one print location.';

      case STATUS.BELOW_MIN:
        return `Our minimum order is ${range.min} pieces. ${this.#bumpButton(range.min, `Change to ${range.min}`)}`;

      case STATUS.ABOVE_MAX:
        return escapeHtml(this.#pricing.text.overMaxMessage || '');

      case STATUS.COLOUR_GATE: {
        const gate = quote.gates[0];
        const noun = gate.locationId ? `${gate.colours}-colour printing` : `${gate.colours} colours in total`;
        if (!gate.minQty) {
          return `We can’t offer ${escapeHtml(noun)} at this size — send your details and we’ll quote it.`;
        }
        return `${escapeHtml(noun)} starts at ${gate.minQty} pieces. ${this.#bumpButton(
          quote.suggestedQty || gate.minQty,
          `Change to ${quote.suggestedQty || gate.minQty}`
        )}`;
      }

      default:
        return '';
    }
  }

  #bumpButton(qty, label) {
    return `<button type="button" class="iq-bump" data-iq-bump-qty="${qty}">${escapeHtml(label)}</button>`;
  }

  #renderGates(quote) {
    const gateByLocation = {};
    (quote.gates || []).forEach((g) => {
      if (g.locationId) gateByLocation[g.locationId] = g;
    });

    this.querySelectorAll('[data-iq-location]').forEach((row) => {
      const message = row.querySelector('[data-iq-gate-message]');
      if (!message) return;
      const gate = gateByLocation[row.dataset.iqLocation];

      if (gate && gate.minQty) {
        message.textContent = `${gate.colours} colours need at least ${gate.minQty} pieces.`;
        message.hidden = false;
        row.setAttribute('data-iq-gated', '');
      } else {
        message.hidden = true;
        row.removeAttribute('data-iq-gated');
      }
    });

    const bracket = findBracket(this.#pricing, this.#state.quantity);
    this.querySelectorAll('[data-iq-color-input]').forEach((input) => {
      const colours = parseInt(input.value, 10);
      const available = bracket
        ? bracket.rates[colours - 1] !== null && bracket.rates[colours - 1] !== undefined
        : false;
      const swatch = input.closest('.iq-swatch');
      if (swatch) swatch.classList.toggle('iq-swatch--unavailable', !available);

      const noun = `${colours} colour${colours > 1 ? 's' : ''}`;
      if (available) {
        input.setAttribute('aria-label', noun);
      } else {
        const min = minQuantityForColours(this.#pricing, colours);
        input.setAttribute(
          'aria-label',
          min ? `${noun}, needs at least ${min} pieces` : `${noun}, not available`
        );
      }
    });

    const byId = {};
    quote.lines.forEach((line) => {
      byId[line.id] = line;
    });
    this.querySelectorAll('[data-iq-location]').forEach((row) => {
      const priceEl = row.querySelector('[data-iq-location-price]');
      if (!priceEl) return;
      const line = byId[row.dataset.iqLocation];
      priceEl.textContent = line ? this.#money.format(line.lineCents / 100) : '';
    });
  }

  #renderInk(quote) {
    const el = this.querySelector('[data-iq-ink-amount]');
    if (!el || !this.#pricing.ink || !this.#pricing.ink.enabled) return;

    if (quote.status !== STATUS.OK || !quote.surchargeBaseCents) {
      el.textContent = '';
      return;
    }

    const cents = Math.round((quote.surchargeBaseCents * this.#pricing.ink.percent) / 100);
    el.textContent = this.#money.format(cents / 100);
  }

  #describeSelections(quote) {
    const parts = [];
    const garment = (this.#pricing.garments || []).find((g) => g.id === this.#state.garmentId);
    if (garment) parts.push(`Garment: ${garment.label}`);
    if (this.#state.garmentColor) {
      parts.push(`${this.#state.garmentColorLabel}: ${this.#state.garmentColor}`);
    }
    parts.push(`Quantity: ${quote.qty} pieces`);

    const sizeParts = this.#sizeParts();
    if (sizeParts.length) parts.push(`Sizes: ${sizeParts.join(', ')}`);

    const locations = (this.#pricing.locations || [])
      .filter((loc) => (this.#state.locations[loc.id] || 0) > 0)
      .map((loc) => `${loc.label}: ${this.#state.locations[loc.id]} colour(s)`);
    if (locations.length) parts.push(`Print: ${locations.join(', ')}`);

    if (this.#pricing.ink && this.#pricing.ink.enabled) {
      parts.push(
        this.#state.premiumInk
          ? `Ink: ${this.#pricing.ink.label} (+${this.#pricing.ink.percent}% on printing)`
          : `Ink: ${this.#pricing.ink.standardLabel || 'Standard plastisol'}`
      );
    }
    return parts;
  }

  #quoteSummaryText(quote) {
    const lines = this.#describeSelections(quote);

    if (quote.status !== STATUS.OK) {
      lines.push('');
      lines.push('No online price — needs a manual quote.');
      const note = stripTags(this.#statusMessage(quote));
      if (note) lines.push(note);
      return lines.join('\n');
    }

    lines.push('');
    quote.lines.forEach((line) => {
      lines.push(
        `${line.label} — ${line.colours} colour(s) @ ${this.#money.format(
          line.rateCents / 100
        )} × ${quote.qty} = ${this.#money.format(line.lineCents / 100)}`
      );
    });

    quote.garmentLines.forEach((line) => {
      lines.push(
        `${quote.garment.label} (${line.tierLabel}) @ ${this.#money.format(
          line.unitCents / 100
        )} × ${line.count} = ${this.#money.format(line.lineCents / 100)}`
      );
    });
    if (quote.garmentUnsized) lines.push(`Note: ${this.#unsizedNote()}`);
    if (quote.setupCents > 0) {
      lines.push(`Setup (${quote.screens} screens) = ${this.#money.format(quote.setupCents / 100)}`);
    }
    if (quote.surchargeCents > 0) {
      lines.push(
        `${this.#pricing.ink.label} (+${this.#pricing.ink.percent}% on printing) = ${this.#money.format(
          quote.surchargeCents / 100
        )}`
      );
    }

    lines.push('');
    lines.push(`Estimated total: ${this.#money.format(quote.totalCents / 100)}`);
    lines.push(`Per piece: ${this.#money.format(quote.perUnitCents / 100)}`);

    if (this.#pricing.garmentMode !== 'add' && this.#pricing.text.printingOnlyNote) {
      lines.push(`Note: ${this.#pricing.text.printingOnlyNote}`);
    }
    return lines.join('\n');
  }

  #renderRecap(quote) {
    const recap = this.querySelector('[data-iq-recap]');
    if (!recap) return;

    const list = recap.querySelector('[data-iq-recap-list]');
    const total = recap.querySelector('[data-iq-recap-total]');
    const priceable = quote.status === STATUS.OK;

    if (list) {
      list.innerHTML = '';
      this.#describeSelections(quote).forEach((part) => {
        const [key, ...rest] = part.split(':');
        const dt = document.createElement('dt');
        dt.textContent = key;
        const dd = document.createElement('dd');
        dd.textContent = rest.join(':').trim();
        list.append(dt, dd);
      });
    }

    if (total) {
      total.textContent = priceable ? this.#money.format(quote.totalCents / 100) : 'Manual quote';
    }

    recap.hidden = quote.status === STATUS.NO_QUANTITY || quote.status === STATUS.NO_LOCATIONS;
  }

  #watchForm() {
    const mount = this.querySelector('[data-iq-form-mount]');
    if (!mount) return;

    const config = this.#pricing.form || {};
    const wanted = [config.summaryField, config.totalField, config.dataField].filter(
      (name) => name && String(name).trim() !== ''
    );
    if (!wanted.length) return;

    const tryResolve = () => {
      const fields = this.#resolveFormFields(mount);
      if (!fields) return false;
      this.#formFields = fields;
      this.#writeToForm(buildQuote(this.#state, this.#pricing));
      return true;
    };

    if (!tryResolve()) {
      this.#formObserver = new MutationObserver(() => {
        tryResolve();
      });
      this.#formObserver.observe(mount, { childList: true, subtree: true });

      setTimeout(() => {
        if (!this.#formFields) {
          console.warn(
            '[instant-quote] Quote fields not found in the form. Check the field names in Powerful Form Builder match the section settings.'
          );
          this.#reportEditorIssue(
            'The quote fields could not be found in your form. Check that it contains fields named exactly: ' +
              `“${this.#pricing.form.summaryField}”, “${this.#pricing.form.totalField}” and “${this.#pricing.form.dataField}”.`
          );
        }
      }, 8000);
    }

    mount.addEventListener(
      'submit',
      () => {
        this.#formFields = this.#resolveFormFields(mount) || this.#formFields;
        this.#writeToForm(buildQuote(this.#state, this.#pricing));
      },
      true
    );

    mount.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest('button, [type="submit"]')) return;
        this.#formFields = this.#resolveFormFields(mount) || this.#formFields;
        this.#writeToForm(buildQuote(this.#state, this.#pricing));
      },
      true
    );
  }

  #findField(root, needle) {
    if (!needle) return null;
    const target = needle.trim().toLowerCase();
    const candidates = root.querySelectorAll('input, textarea, select');

    for (const field of candidates) {
      const haystacks = [
        field.getAttribute('name'),
        field.getAttribute('id'),
        field.getAttribute('placeholder'),
        field.getAttribute('aria-label'),
        field.dataset ? field.dataset.label : null,
      ];

      const wrapper = field.closest('.globo-form-control, .form-group, label, div');
      if (wrapper) {
        const label = wrapper.querySelector('label');
        if (label) haystacks.push(label.textContent);
      }

      if (haystacks.some((value) => value && value.toLowerCase().includes(target))) {
        return field;
      }
    }
    return null;
  }

  #resolveFormFields(mount) {
    const config = this.#pricing.form || {};
    const summary = this.#findField(mount, config.summaryField);
    const total = this.#findField(mount, config.totalField);
    const data = this.#findField(mount, config.dataField);

    if (!summary && !total && !data) return null;
    return { summary, total, data };
  }

  #writeToForm(quote) {
    if (!this.#formFields) return;
    const priceable = quote.status === STATUS.OK;

    setFieldValue(this.#formFields.summary, this.#quoteSummaryText(quote));
    setFieldValue(
      this.#formFields.total,
      priceable ? this.#money.format(quote.totalCents / 100) : 'Manual quote required'
    );
    setFieldValue(
      this.#formFields.data,
      JSON.stringify({
        status: quote.status,
        quantity: quote.qty,
        sizes: this.#sizeMap(),
        garmentId: this.#state.garmentId,
        garment: quote.garment ? quote.garment.label : null,
        garmentColor: this.#state.garmentColor,
        garmentLines: quote.garmentLines.map(({ tierLabel, count, unitCents, lineCents }) => ({
          price: tierLabel,
          count,
          unitCents,
          lineCents,
        })),
        locations: this.#state.locations,
        premiumInk: this.#state.premiumInk,
        currency: this.#pricing.currency,
        printCents: quote.printCents,
        garmentCents: quote.garmentCents,
        setupCents: quote.setupCents,
        surchargeCents: quote.surchargeCents,
        totalCents: quote.totalCents,
        perUnitCents: quote.perUnitCents,
      })
    );
  }

  #reportEditorIssue(message) {
    if (!document.body.dataset.editorMode || document.body.dataset.editorMode !== 'true') return;
    const host = this.querySelector('[data-iq-form-panel]');
    if (!host || host.querySelector('.instant-quote__editor-warning--runtime')) return;

    const box = document.createElement('div');
    box.className = 'instant-quote__editor-warning instant-quote__editor-warning--runtime';
    box.setAttribute('role', 'status');
    box.innerHTML = `<p class="instant-quote__editor-warning-title">Quote not connected to the form</p><p>${escapeHtml(
      message
    )}</p><p class="instant-quote__editor-warning-note">Only you can see this.</p>`;
    host.prepend(box);
  }
}

function setFieldValue(field, value) {
  if (!field) return;
  if (field.value === value) return;

  field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );
}

function stripTags(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').trim();
}

if (!customElements.get('instant-quote')) {
  customElements.define('instant-quote', InstantQuote);
}

export { buildQuote, minQuantityForColours, findBracket, STATUS };
